import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useState } from "react";
import Modal from "../../../App/Components/Modal/Modal";
import { useArchivedFieldValidation } from "../../../App/Hooks/useArchivedFieldValidation";
import { ICampaignFormState } from "../../../App/Typings/Campaigns";
import { Rule } from "../../../App/Typings/DynamicGroupRules";
import { IGroup } from "../../../App/Typings/Groups";
import { checkForChanges, getRuleCount, MaxRuleCount } from "../../../App/Utilities/dynamicFiltering";
import { DynamicFilterRules } from "../../../DynamicGroup/Components/DynamicFilterRules";
import { CommandHelpContent, FieldHelpContent } from "../../../DynamicGroup/Components/HelpContent";
import { FieldType } from "../../../DynamicGroup/DynamicGroup";
import { DisplayRecursiveFilterChips } from "./FilterChip";

const duplicateRules = (rules: Rule[]) => {
  return rules.map((rule) => {
    const newRule = _.cloneDeep(rule);
    if (newRule.rules && newRule.rules.length > 0) {
      newRule.rules = duplicateRules(newRule.rules);
    }
    return newRule;
  });
};

export default function FilterModal({
  queryRules,
  formFields = [],
  isOpen,
  handleSetRules,
  handleOpenModal,
  handleCloseModal,
  handleResetRules,
  handleRemoveRule,
  LoadSaveFilters,
  isCampaign,
  campaignId,
  tags,
  campaigns,
  groups,
  isLoading,
}: {
  queryRules: Rule[];
  formFields?: FieldType[];
  isOpen: boolean;
  handleSetRules: (rules: Rule[]) => void;
  handleOpenModal: () => void;
  handleCloseModal: () => void;
  handleResetRules: () => void;
  handleRemoveRule: (ruleId: string) => void;
  LoadSaveFilters?: JSX.Element;
  isCampaign: boolean;
  campaignId?: string;
  tags: string[];
  campaigns: ICampaignFormState[];
  groups: IGroup[];
  isLoading: boolean;
}) {
  const [pendingRules, setPendingRules] = useState<Rule[]>([]);
  const displayRules = queryRules.filter((r) => r.filter.value !== "" && r.filter.field !== "");
  const currentRuleCount = displayRules.reduce((acc, rule) => acc + getRuleCount(rule), 0);
  const storageKey = campaignId ? `showArchivedFields.${campaignId}` : "showArchivedFields";

  const {
    availableCampaignFields,
    archivedFields,
    archivedFieldIds,
    showArchivedFields,
    toggleArchivedFields,
    filteredFormFields,
    hasValidState,
    hasValidRuleCount,
    currentRulesHaveArchivedFields,
    enableModalApplyFilters,
  } = useArchivedFieldValidation({
    storageKey,
    rules: pendingRules,
    formFields,
    includeRuleCount: true,
  });

  useEffect(() => {
    setPendingRules(duplicateRules(queryRules));
  }, [queryRules]);

  const handleApplyFilters = () => {
    const hasChanges = checkForChanges(pendingRules, queryRules);
    if (hasChanges && enableModalApplyFilters) {
      handleSetRules(duplicateRules(pendingRules));
    }
    handleCloseModal();
  };

  const handleClearFilters = () => {
    handleResetRules();
    setPendingRules([
      {
        filter: {
          command: "",
          field: "",
          type: "",
          value: "",
        },
        id: "initial-rule",
        rules: [],
      },
    ]);
  };

  const handleRemoveRuleAndPending = (ruleId: string) => {
    const newRules = queryRules.filter((r) => r.id !== ruleId);
    if (newRules.length === 0) {
      handleClearFilters();
    } else {
      handleRemoveRule(ruleId);
      setPendingRules((rules) => rules.filter((r) => r.id !== ruleId));
    }
  };

  return (
    <>
      <Grid item xs={12} md={6} container gap={2} wrap="wrap" alignItems="center">
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" onClick={handleOpenModal} data-test="open-filter-modal">
            {currentRuleCount > 0 ? "Edit Filters" : "Filter"}
          </Button>
          {currentRuleCount > 0 && (
            <Button onClick={handleClearFilters} data-test="clear-campaign-filters">
              Clear All
            </Button>
          )}
          {LoadSaveFilters}
        </Box>
      </Grid>
      <Grid item xs={12} container gap={2} sx={{ mb: 1 }}>
        <Stack flexDirection="row" flexWrap="wrap" gap={1}>
          <DisplayRecursiveFilterChips
            isLoading={isLoading}
            forms={campaigns}
            groups={groups}
            displayRules={displayRules}
            formFields={availableCampaignFields}
            handleRemoveRule={handleRemoveRuleAndPending}
          />
        </Stack>
      </Grid>
      <Modal
        size="xl"
        title="Filter Form Responses"
        isOpen={isOpen}
        handleCloseModal={handleCloseModal}
        dataTestId="campaign-responses-filter-modal"
        content={
          <Grid container spacing={2}>
            <DynamicFilterRules
              groups={groups}
              rules={pendingRules}
              onChange={(rules) => setPendingRules(rules)}
              fieldOptions={filteredFormFields}
              isCampaign={isCampaign}
              helpTitle="Filter Help"
              helpId="campaign-responses-filter-help"
              tags={tags}
              campaigns={campaigns}
              archivedFieldIds={archivedFieldIds}
              showArchivedFieldError={!showArchivedFields}
              FieldHelpContent={
                <FieldHelpContent
                  headerContent={
                    <Typography variant="body2">
                      Fields are candidate information that you can search on such as their name, location or
                      answers to custom questions.
                    </Typography>
                  }
                />
              }
              CommandHelpContent={
                <CommandHelpContent
                  headerContent={
                    <>
                      <Typography variant="body2">
                        Commands are operations you can perform on a field to find candidates.
                      </Typography>
                      <Typography variant="body2">
                        The commands can change based on the field you are searching on.
                      </Typography>
                    </>
                  }
                />
              }
            />
          </Grid>
        }
        footerContent={
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={{ width: "100%" }}
          >
            {archivedFields.length > 0 && (
              <Box sx={{ ml: 2, flexBasis: "auto" }}>
                <FormControlLabel
                  label="Include Archived Form Fields"
                  data-test="toggle-archived-form-fields"
                  control={
                    <Switch
                      checked={showArchivedFields}
                      size="small"
                      onChange={toggleArchivedFields}
                      color="info"
                    />
                  }
                  sx={{
                    "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" },
                  }}
                />
              </Box>
            )}
            <Stack
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              sx={{ width: "100%" }}
            >
              {!hasValidRuleCount && (
                <FormHelperText error data-test="error-invalid-rule-count">
                  You can only apply up to {MaxRuleCount} filters. Please remove some filters to proceed.
                </FormHelperText>
              )}
              {!hasValidState && (
                <FormHelperText error data-test="error-invalid-rule">
                  Please ensure all filters have a valid value before applying.
                </FormHelperText>
              )}
              {currentRulesHaveArchivedFields && !showArchivedFields && (
                <FormHelperText error data-test="error-archived-fields-selected">
                  Remove archived fields from filters or enable "Include Archived Form Fields" to apply.
                </FormHelperText>
              )}
              <Button
                data-test="filter-modal-save"
                variant="contained"
                onClick={handleApplyFilters}
                disabled={!enableModalApplyFilters}
              >
                Apply Filters
              </Button>
              <Button data-test="filter-modal-cancel" variant="outlined" onClick={handleCloseModal}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        }
      />
    </>
  );
}
